import { type CriteriaFilterValue } from "@stacklet/ui";

import { createMultipleFilter } from "app/utils/filters";

import { type RulesFilterFormData } from "../components/schema";
import { criteriaFilterValuesToFilterElement } from "./criteriaFilterUtils";

import type { NotificationRulePreviewInput } from "../hooks/__generated__/useNotificationRulePreviewQuery.graphql";
import type {
  AddNotificationRuleInput,
  JiraAssigneeSelectionType,
  JiraSpaceSelectionType,
} from "app/mutations/__generated__/AddNotificationRuleMutation.graphql";
import type { UpdateNotificationRuleInput } from "app/mutations/__generated__/UpdateNotificationRuleMutation.graphql";
import type { FilterElementInput } from "app/utils/filters";

const DEFAULT_UPDATE_SCHEDULE = "rate(1 day)";
const PREVIEW_GROUP_LIMIT = 10;

export type FilterFormFields = Pick<
  RulesFilterFormData,
  | "providerFilters"
  | "accountFilters"
  | "resourceFilters"
  | "policyFilters"
  | "bindingFilters"
>;

export function buildFilterOperands(
  formData: FilterFormFields,
): FilterElementInput[] {
  const normalizedProviderFilters = normalizeProviderFilters(
    formData.providerFilters ?? [],
  );
  return [
    ...flattenToSingleFilters(
      criteriaFilterValuesToFilterElement(normalizedProviderFilters),
    ),
    ...flattenToSingleFilters(
      criteriaFilterValuesToFilterElement(formData.accountFilters ?? []),
    ),
    ...flattenToSingleFilters(
      criteriaFilterValuesToFilterElement(formData.resourceFilters ?? []),
    ),
    ...flattenToSingleFilters(
      criteriaFilterValuesToFilterElement(formData.policyFilters ?? []),
    ),
    ...flattenToSingleFilters(
      criteriaFilterValuesToFilterElement(formData.bindingFilters ?? []),
    ),
  ];
}

/**
 * Builds a NotificationRulePreviewInput from the current form data.
 * Returns null if no filters are selected (preview requires at least one filter).
 */
export function buildNotificationRuleFilterInput(
  formData: FilterFormFields &
    Pick<
      RulesFilterFormData,
      | "jiraGrouping"
      | "includeCostDataByDefault"
      | "jiraInstance"
      | "jiraSpace"
      | "jiraWorkType"
      | "jiraAssignee"
      | "issueDescription"
    >,
): NotificationRulePreviewInput | null {
  const filterOperands = buildFilterOperands(formData);

  if (filterOperands.length === 0) {
    return null;
  }

  const combinedFilter = createMultipleFilter(filterOperands, "AND") ?? {
    single: { name: "provider", value: "aws" },
  };

  const groupBy = formData.jiraGrouping ?? [];
  const includeCostData = formData.includeCostDataByDefault ?? false;

  const jiraTarget: NotificationRulePreviewInput["jiraTarget"] =
    formData.jiraInstance &&
    formData.jiraSpace?.length &&
    formData.jiraWorkType?.length
      ? {
          instanceUUID: formData.jiraInstance.uuid,
          space: {
            type: "SPACE_KEY" as JiraSpaceSelectionType,
            value: formData.jiraSpace[formData.jiraSpace.length - 1],
          },
          workType: formData.jiraWorkType[0],
          assignee: formData.jiraAssignee
            ? {
                type: formData.jiraAssignee.type as JiraAssigneeSelectionType,
                value: formData.jiraAssignee.value ?? null,
              }
            : null,
          description: formData.issueDescription ?? null,
        }
      : null;

  return {
    filter: combinedFilter,
    groupBy,
    includeCostData,
    numberOfGroups: PREVIEW_GROUP_LIMIT,
    jiraTarget,
  };
}

function normalizeProviderFilters(
  providerFilters: CriteriaFilterValue[] | null | undefined,
): CriteriaFilterValue[] {
  const filters = providerFilters ?? [];
  if (filters.length <= 1) {
    return filters;
  }

  const allProviderCriteria = filters.every(
    (filter) => filter.criteria === "provider",
  );
  if (!allProviderCriteria) {
    return filters;
  }

  const hasExplicitIsOneOf = filters.some(
    (filter) =>
      filter.operator?.trim().toLowerCase().replace(/\s+/g, "-") ===
      "is-one-of",
  );
  if (hasExplicitIsOneOf) {
    return filters;
  }

  const providerValues = Array.from(
    new Map(
      filters
        .flatMap((filter) =>
          (filter.value ?? []).flatMap((value) =>
            value?.id ? [[value.id, value.name ?? value.id] as const] : [],
          ),
        )
        .map(([id, name]) => [id, { id, name }]),
    ).values(),
  );

  if (providerValues.length === 0) {
    return filters;
  }

  return [
    {
      criteria: "provider",
      name: "provider",
      operator: "is-one-of",
      value: providerValues,
    },
  ];
}

/**
 * Flattens a FilterElementInput (single or multiple with nested operands) into an array
 * of filter elements at one depth. NOT-wrapped and OR-wrapped elements are preserved
 * intact since they represent structured filter semantics (e.g. NOT(single(equals)) for
 * scalar negation, NOT(OR(equals, ...)) for is-not-one-of) that must reach the API as-is.
 */
function flattenToSingleFilters(
  element: FilterElementInput | null | undefined,
): FilterElementInput[] {
  if (!element) return [];
  if (element.single) return [{ single: element.single }];
  if (element.multiple?.operands) {
    const { operator, operands } = element.multiple;
    // Only flatten AND groups; preserve OR, NOT, and any future operators intact
    // since they represent structured filter semantics (is-one-of, negation, etc.)
    if (operator?.toUpperCase() !== "AND") {
      return [element];
    }
    return operands.flatMap(flattenToSingleFilters);
  }
  return [];
}

/**
 * Transforms form data into AddNotificationRuleInput for the mutation.
 * groupBy is taken directly from the form; JiraGrouping already converts
 * dropdown selections to the final string[] before calling onChange.
 */
export function formatNotificationRuleInput(
  formData: RulesFilterFormData,
  projectUUID: string,
  name: string,
): AddNotificationRuleInput {
  const filterOperands = buildFilterOperands(formData);

  // Create combined filter - form validation ensures at least one filter is present
  const combinedFilter = createMultipleFilter(filterOperands, "AND") ?? {
    single: {
      name: "provider",
      value: "aws",
    },
  };

  const groupBy = formData.jiraGrouping ?? [];

  // Format updateSchedule from createNewIssuesForResources
  // If true, create issues once a day (daily at noon)
  // If false, null means instant/on-demand
  const updateSchedule = formData.createNewIssuesForResources
    ? DEFAULT_UPDATE_SCHEDULE
    : null;

  // Format includeCostData
  const includeCostData = formData.includeCostDataByDefault ?? false;

  // Format Jira target - validation is handled by form schema.
  // Note: jiraIfIssueClosed (Closed Status) is collected in the form but NotificationRuleJiraTargetInput
  // has no corresponding field in the API; it is not sent.
  const jiraTarget: AddNotificationRuleInput["jiraTarget"] | null =
    formData.jiraInstance &&
    formData.jiraSpace?.length &&
    formData.jiraWorkType?.length
      ? {
          instanceUUID: formData.jiraInstance.uuid,
          space: {
            // Note: The API supports multiple space selection types (SPACE_KEY, RESOURCE_TAG, ACCOUNT_TAG, ACCOUNT_VARIABLE),
            // but the UI currently only supports selecting literal space keys. If support for other types is needed,
            // the form data structure and JiraSpace component would need to be updated similar to how JiraAssignee works.
            type: "SPACE_KEY" as JiraSpaceSelectionType,
            value: formData.jiraSpace[formData.jiraSpace.length - 1],
          },
          workType: formData.jiraWorkType[0],
          assignee: formData.jiraAssignee
            ? {
                type: formData.jiraAssignee.type as JiraAssigneeSelectionType,
                value: formData.jiraAssignee.value ?? null,
              }
            : null,
          description: formData.issueDescription ?? null,
        }
      : null;

  return {
    projectUUID,
    name,
    description: null,
    filter: combinedFilter,
    groupBy,
    includeCostData,
    updateSchedule,
    jiraTarget,
  };
}

/**
 * Builds UpdateNotificationRuleInput from form data for the update mutation.
 * Reuses the same filter, groupBy, schedule, and Jira target logic as add.
 */
export function formatUpdateNotificationRuleInput(
  formData: RulesFilterFormData,
  ruleUuid: string,
): UpdateNotificationRuleInput {
  const addShape = formatNotificationRuleInput(
    formData,
    "", // projectUUID not used for update payload
    formData.name,
  );
  return {
    uuid: ruleUuid,
    name: addShape.name,
    description: formData.description ?? null,
    filter: addShape.filter,
    groupBy: addShape.groupBy,
    includeCostData: addShape.includeCostData,
    updateSchedule: addShape.updateSchedule,
    jiraTarget: addShape.jiraTarget,
  };
}
